import React, { useRef, useEffect, useState } from 'react';

import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { GalleryItem } from '../Career/types';
import DOMPurify from "dompurify";
// components
import { Post2 } from 'components/blog';
import ContactForm from './ContactPost';
import { Helmet } from 'react-helmet-async';
import { getAllBlogs } from 'helpers/api/BlogService';
import { Post1 } from 'components/blog';
import BlogImage2 from 'assets/images/hero/New folder/BlogImage2.png';

type PostContentProps = {
    gallery: GalleryItem[];
    post: Post2 | null;
};


interface BlogPost {
    id: number;
    blogTitle: string;
    shortDescription: string;
    status: number;
    blogTags: string;
    blogImage: string;
    createdOn: string;
    metaKeywords: string
}




const PostContent = ({ gallery, post }: PostContentProps) => {

    const splitDescription = (htmlContent: string) => {
        if (!htmlContent) return { firstPart: '', secondPart: '' };

        const paragraphs = htmlContent.split('</p>');

        const splitIndex = Math.min(4, paragraphs.length - 1);
        const firstPart = paragraphs.slice(0, splitIndex).join('</p>') + '</p>';
        const secondPart = paragraphs.slice(splitIndex).join('</p>');

        return { firstPart, secondPart };
    };

    const { firstPart, secondPart } = splitDescription(post?.longDescription || '');

    console.log('post', post);


    const sanitizeAndFixHeadings = (html: string) => {
        let clean = DOMPurify.sanitize(html);
        clean = clean.replace(/<h1>/g, "<h2>").replace(/<\/h1>/g, "</h2>");
        return clean;
    };

    const sanitizedFirstPart = sanitizeAndFixHeadings(firstPart);
    const sanitizedSecondPart = sanitizeAndFixHeadings(secondPart);


    // const sanitizedFirstPart = DOMPurify.sanitize(firstPart);
    // const sanitizedSecondPart = DOMPurify.sanitize(secondPart);

    const leftColRef = useRef<HTMLDivElement>(null);
    const rightColRef = useRef<HTMLDivElement>(null);
    const rightInnerRef = useRef<HTMLDivElement>(null);
    const [stackBelow, setStackBelow] = useState(false);
    const [leftTaller, setLeftTaller] = useState(false);
    const [formCompleted, setFormCompleted] = useState(false);
    const [collapseRight, setCollapseRight] = useState(false);
    const [allBlogList, setAllBlogList] = useState<Post1[]>([]);


    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await getAllBlogs();


                if (response?.success && Array.isArray(response.result)) {
                    const activeBlogs = response.result.filter((blog: any) => blog.status === 1);

                    const transformedData: Post1[] = activeBlogs.map((blog: any) => ({
                        id: blog.id,
                        title: blog.blogTitle,
                        description: blog.shortDescription,
                        image: `${blog.blogImage}`,
                        tag: blog.blogTags,
                        createdOn: blog.createdOn,
                        metaKeywords: blog.metaKeywords
                    }));

                    const relatedBlogs = transformedData.filter(item => item.id !== post?.id);
                    console.log('relatedBlogs', relatedBlogs);
                    setAllBlogList(relatedBlogs);
                } else {
                    console.error('Invalid response format:', response);
                }
            } catch (error) {
                console.error('Failed to fetch blogs:', error);
            }
        };

        fetchBlogs();
    }, []);


    // when collapse animation ends, mark form as completed to let blog expand
    useEffect(() => {
        if (!collapseRight) return;
        const el = rightInnerRef.current || rightColRef.current;
        if (!el) {
            setFormCompleted(true);
            return;
        }

        const onTransitionEnd = (e: TransitionEvent) => {
            if ((e as any).propertyName === 'max-height' || (e as any).propertyName === 'height') {
                setFormCompleted(true);
                setCollapseRight(false);
            }
        };

        el.addEventListener('transitionend', onTransitionEnd as any);
        // safety timeout in case transitionend doesn't fire
        const t = setTimeout(() => {
            setFormCompleted(true);
            setCollapseRight(false);
        }, 700);

        return () => {
            clearTimeout(t);
            el.removeEventListener('transitionend', onTransitionEnd as any);
        };
    }, [collapseRight]);

    // measure heights and decide whether to render blog data in its own full-width row
    useEffect(() => {
        if (formCompleted) {
            // when form is gone we don't need to measure
            setStackBelow(false);
            return;
        }

        const measure = () => {
            const leftH = leftColRef.current?.offsetHeight || 0;
            const rightH = rightColRef.current?.offsetHeight || 0;
            setStackBelow(rightH > leftH);
            // add small threshold to avoid flicker
            setLeftTaller(leftH > rightH + 80);
        };

        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [post, firstPart, secondPart, formCompleted]);

    if (!post) {
        return (
            <Container className="text-center py-5">
                <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    return (

        <>
            <Helmet>
                <title>{`${post?.title} | Sales Trackify CRM & Sales Management`}</title>
                <meta name="description" content={`${post?.shortDescription}`} />

                <meta name="keywords" content={post?.metaKeywords || `${post?.blogTags}`} />

                <meta property="og:title" content={post?.title} />
                <meta property="og:description" content={post?.shortDescription} />
                <meta property="og:image" content={post?.blogImage} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={post?.title} />
                <meta name="twitter:description" content={post?.shortDescription} />
                <meta name="twitter:image" content={post?.blogImage} />
            </Helmet>

            <section className="position-relative pb-5">
                <Container fluid={formCompleted}>
                    <Row className={`mb-5 ${!formCompleted ? 'two-column-row' : ''}`} style={{ alignItems: 'flex-start' }}>
                        <Col
                            lg={formCompleted ? 12 : 7}
                            ref={leftColRef}
                            className={`${formCompleted ? 'blog-fullwidth' : ''} left-column`}
                        >
                            <div className={!formCompleted ? 'left-scroll' : ''}>
                                <div className="text-black mb-4 fs-17 mt-4">
                                    {post?.shortDescription}
                                </div>

                                {(!stackBelow || formCompleted) && post.blogImage && post.blogImage.trim() !== '' && (
                                    <figure className="figure text-center mb-4 mt-4">
                                        <img
                                            src={post.blogImage}
                                            alt="Blog"
                                            className="figure-img img-fluid rounded shadow"
                                            style={{
                                                maxWidth: '100%',
                                                height: 'auto',
                                                maxHeight: '400px',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </figure>
                                )}

                                <div className={formCompleted ? 'two-column-content' : ''}>
                                    {firstPart && (
                                        <div className="mb-4">
                                            <div
                                                dangerouslySetInnerHTML={{ __html: sanitizedFirstPart }}
                                            />
                                        </div>
                                    )}
                                    {(!stackBelow || formCompleted) && secondPart && (
                                        <div className="mt-4">
                                            <div
                                                dangerouslySetInnerHTML={{ __html: sanitizedSecondPart }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>

                        {!formCompleted && (
                            <Col lg={5} ref={rightColRef} className="right-column">
                                {/* <div ref={rightInnerRef} 
                                className={collapseRight ? 'collapsing-right closing border-wrapper' : 'collapsing-right border-wrapper'} style={{ top: '100px' }}> */}
                                {/* <div
                                    ref={rightInnerRef}
                                    className={
                                        collapseRight
                                            ? 'collapsing-right closing border-wrapper sticky-form'
                                            : 'collapsing-right border-wrapper sticky-form'
                                    }
                                >
                                    <div className="border border-primary">
                                        <ContactForm />
                                    </div>
                                </div> */}
                                <div>
                                    <div
                                        ref={rightInnerRef}
                                        className={
                                            collapseRight
                                                ? 'collapsing-right closing border-wrapper sticky-form'
                                                : 'collapsing-right border-wrapper sticky-form'
                                        }
                                    >
                                        <div className="border border-primary">
                                            <ContactForm />
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        )}
                    </Row>


                    {stackBelow && post.blogImage && (
                        <Row className="mb-5">
                            <Col lg={12}>
                                <figure className="figure text-center mb-4">
                                    <img
                                        src={post.blogImage}
                                        alt="Blog"
                                        className="figure-img img-fluid rounded shadow"
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            maxHeight: '400px',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </figure>
                            </Col>
                        </Row>
                    )}

                    {stackBelow && secondPart && (
                        <Row>
                            <Col lg={12}>
                                <div className="mt-4">
                                    <div
                                        dangerouslySetInnerHTML={{ __html: sanitizedSecondPart }}
                                    />
                                </div>
                            </Col>
                        </Row>
                    )}
                </Container>
            </section>

            {/* ===================== RELATED BLOGS SECTION ===================== */}
            {allBlogList.length > 0 && (
                <section className="py-5 bg-light">
                    <Container>
                        <h2 className="mb-4 fw-bold">Related Blogs</h2>
                        <Row>
                            {allBlogList.slice(0, 3).map((blog) => (
                                <Col md={4} className="mb-4" key={blog.id}>
                                    <div className="related-card shadow-sm rounded overflow-hidden bg-white h-100">
                                        {/* Blog Image */}
                                        <div className="related-img-wrapper">
                                            <img
                                                src={blog.image || BlogImage2}
                                                alt={blog.title}
                                                className="img-fluid"
                                                style={{ width: "100%", height: "200px", objectFit: "cover" }}
                                            />
                                            {blog.tag && (
                                                <span className="related-tag">
                                                    {/* {blog.tag.split(",")[0]} */}
                                                    {blog.tag}
                                                </span>
                                            )}
                                        </div>

                                        {/* Blog Content */}
                                        <div className="p-3">
                                            <small className="text-muted">
                                                {new Date(blog.createdOn).toDateString()}
                                            </small>

                                            <h5 className="mt-2 fw-bold">{blog.title}</h5>

                                            <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                                                {blog.description?.slice(0, 120)}...
                                            </p>

                                            <a href={`/blog/${blog.id}`} className="text-primary fw-semibold">
                                                Read More →
                                            </a>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>
            )}

            {/* extra styles for post layout adjustments */}
            <style>{`
                /* when the form is complete we allow content to flow in two columns */
                .two-column-content {
                    column-count: 2;
                    column-gap: 2rem;
                }

                /* optionally remove any max-width imposed by container when fluid */
                .blog-fullwidth {
                    width: 100%;
                }
                /* collapsing right column animation (applies to inner wrapper) */
                .border-wrapper {
                    display: block;
                }

                .collapsing-right {
                    max-height: 2000px;
                    // overflow: hidden;
                    transition: max-height 0.45s ease, opacity 0.35s ease, padding 0.35s ease;
                    opacity: 1;
                }
              .sticky-form {
                  position: sticky;
                  top: 100px; /* adjust as needed */
                  height: fit-content;
                }
                .collapsing-right.closing {
                    max-height: 0 !important;
                    opacity: 0;
                    padding-top: 0 !important;
                    padding-bottom: 0 !important;
                }

                .related-card {
                  border-radius: 12px;
                  transition: transform 0.2s ease;
                }

             .related-card:hover {
              transform: translateY(-5px);
             }

         .related-tag {
           background: #0d6efd;
           color: white;
           padding: 4px 10px;
           font-size: 12px;
           border-radius: 8px;
           position: absolute;
           top: 15px;
           left: 15px;
           }

          .related-img-wrapper {
            position: relative;
            }
            `}</style>

        </>
    );
};

export default PostContent;